import React, { useEffect, useState, Suspense } from 'react';
import './GradeCard.css';
import FundHoldings from './FundHoldings';
import WatchButton from './WatchButton';
import TickerNews from './news/TickerNews';
import { labelStep, pillarStep } from '../utils/convictionLabel';

// Recharts is ~500KB and this card renders on the LANDING page, so a static
// import put the whole charting library in front of every ad click for a
// chart that only appears if they press the toggle.
const FundamentalsChart = React.lazy(() => import('./FundamentalsChart'));

// Single-ticker "grade view" (Tier-1 redesign): verdict-first hierarchy —
// conviction ring + label chip + plain-English read, then pillar cards each with
// a verdict chip + one-line "so what", the DCF fair-value read, and an
// expandable per-test breakdown ("show the work"). Shared by the hero widget and
// the logged-out Screener so grades look identical everywhere.
//
// 2026-09-11: the Grade Watch bell sits next to the ticker. Looking at a grade is
// the natural moment to want alerts on it. WatchButton renders nothing when
// logged out or when there's no GradeWatchProvider — which matters here, because
// LoginGate returns PublicScreener OUTSIDE the provider tree.
const RADIUS = 54;
const CIRC = 2 * Math.PI * RADIUS; // 339.29

const PILLAR_META = [
  { key: 'quality', label: 'Quality', max: 100 },
  { key: 'setup', label: 'Setup', max: 100 },
  { key: 'convex', label: 'Convex', max: 5 },
  { key: 'macro', label: 'Macro', max: 5 },
];

// Short, honest band summaries per pillar (a label for the score band, not
// fabricated data). Keyed [pillar][band].
const PILLAR_PHRASE = {
  quality: { fav: 'Strong fundamentals — margins, returns, balance sheet', neu: 'Mixed fundamentals — some strengths, some gaps', cau: 'Soft on the core fundamentals right now' },
  setup: { fav: 'Trend and timing look favorable', neu: 'Average setup — trend intact, momentum mixed', cau: 'Technicals are working against it' },
  convex: { fav: 'Real asymmetric upside in the setup', neu: 'Modest upside — few free options priced in', cau: 'Little asymmetric upside at this price' },
  macro: { fav: 'Market backdrop is supportive', neu: 'Neutral market backdrop', cau: 'Macro conditions are a headwind' },
};

// ETFs / funds / crypto aren't scored on company fundamentals, so use neutral,
// non-equity wording for their pillar one-liners.
const GENERIC_PHRASE = { fav: 'Scores well on this dimension', neu: 'Middle of the pack here', cau: 'Scores poorly on this dimension' };

const ASSET_BADGE = { etf: 'ETF', mutual_fund: 'Fund', crypto: 'Crypto' };

function compactUSD(v) {
  const a = Math.abs(v);
  if (a >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}
function compactNum(v) {
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return `${v.toFixed(0)}`;
}

function bandClass(pct) { return pct >= 60 ? 'fav' : pct >= 40 ? 'neu' : 'cau'; }
function bandColor(cls) { return cls === 'fav' ? '#2FD3A6' : cls === 'neu' ? '#F5B23B' : '#F87171'; }
// Pillar chips: Strong / Mixed / Weak, so "Favorable" only ever means conviction 78+.
function bandWord(cls) { return pillarStep(cls === 'fav' ? 100 : cls === 'neu' ? 50 : 0).word; }
// grade.label is one of the five conviction labels (utils/convictionLabel.js).
// The old includes('favor') test would have matched "Unfavorable" too.
function labelClass(label) { return labelStep(label).cls; }

// Format a test's raw inputs (the actual numbers behind the score) honestly —
// humanized key + rounded value, no percent-guessing that could mislabel.
function formatRaw(raw) {
  if (!raw || typeof raw !== 'object') return [];
  const out = [];
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v !== 'number' || !isFinite(v)) continue;
    const name = k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const a = Math.abs(v);
    let val;
    if (Number.isInteger(v)) val = v.toLocaleString();
    else if (a < 1) val = v.toFixed(3);
    else if (a < 1000) val = v.toFixed(2);
    else val = Math.round(v).toLocaleString();
    out.push({ name, val });
    if (out.length >= 6) break;
  }
  return out;
}

function emptyPillarNote(assetType, pillarLabel) {
  if (assetType === 'etf') return `ETFs are graded on their holdings and structure, so the per-company ${pillarLabel} tests don’t apply — the score reflects the fund as a whole.`;
  if (assetType === 'crypto') return `Crypto is scored on a different model, so these per-company ${pillarLabel} tests don’t apply.`;
  if (assetType === 'mutual_fund') return `Mutual funds are graded on holdings and structure, so the per-company ${pillarLabel} tests don’t apply.`;
  return `These per-company ${pillarLabel} tests aren’t applicable to this asset.`;
}

// `teaser` = this card is being shown to a logged-out visitor, so the caller
// renders its own locked/unlock treatment for the account-only extras (the
// overlay chart) instead of the live toggle. The grade itself is never held
// back — the whole point of the free grade is that the work is visible.
// `showNews` is off only on the landing hero, whose layout is tuned for the
// page ads land on; every other card shows the ticker's latest headlines.
export default function GradeCard({ grade, chartOpen, teaser, showNews = true }) {
  const [expanded, setExpanded] = useState(null);   // which pillar is open
  const [openTest, setOpenTest] = useState(null);    // which test's "why" is open
  const [showChart, setShowChart] = useState(!!chartOpen); // price-vs-fundamentals deep dive
  const [mounted, setMounted] = useState(false);     // drives the ring draw-in

  const key = grade && grade.ticker;
  useEffect(() => {
    setMounted(false);
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, [key]);

  if (!grade) return null;

  const at = grade.asset_type;
  const canChart = !at || at === 'equity';
  const isEquity = !at || at === 'equity';
  const assetNote = at === 'crypto'
    ? 'Scored on AlphaGrade’s crypto model: token supply, liquidity, volatility and fee economics for Quality and Convex, trend and momentum for Setup. Company fundamentals (earnings, DCF) don’t apply.'
    : at === 'etf'
    ? 'Graded on the fund’s cost, liquidity, and structure — not company fundamentals.'
    : at === 'mutual_fund'
    ? 'Graded on the fund’s cost, structure, and holdings — not company fundamentals.'
    : '';
  const conviction = Math.round(grade.conviction);
  const ringOff = mounted ? CIRC * (1 - Math.max(0, Math.min(100, conviction)) / 100) : CIRC;
  const lclass = labelClass(grade.label);

  // The backend judges the DCF against the price shown (public_grade.dcf_verdict).
  const dcf = grade.dcf;
  const up = dcf ? dcf.upside_pct : null;
  const dcfColor = !dcf ? '#93A2BC' : up >= 5 ? '#2FD3A6' : (dcf.above_pct ?? 0) >= 5 ? '#F87171' : '#93A2BC';
  const dcfVerdict = dcf ? (dcf.verdict || '') : '';

  return (
    <div className="gv">
      {/* Identity + price */}
      <div className="gv-head">
        <div className="gv-idwrap">
          <img
            className="gv-logo"
            src={`/api/public/logo/${grade.ticker}`}
            alt=""
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="gv-id">
            <span className="gv-ticker">
              {grade.ticker}
              {ASSET_BADGE[at] && <span className="gv-asset">{ASSET_BADGE[at]}</span>}
              <WatchButton symbol={grade.ticker} className="gv-watch" />
            </span>
            <span className="gv-name">{grade.name}</span>
          </div>
        </div>
        {grade.price != null && (
          <div className="gv-px">
            <div className="gv-px-val">
              ${Number(grade.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            {grade.change_pct != null && (
              <div className="gv-px-chg" style={{ color: grade.change_pct >= 0 ? '#2FD3A6' : '#F87171' }}>
                {grade.change_pct >= 0 ? '+' : ''}{grade.change_pct}%
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conviction ring + verdict-first read */}
      <div className="gv-hero">
        <div className="gv-gauge-col">
          <div className="gv-gauge">
            <svg viewBox="0 0 148 148" role="img" aria-label={`Conviction ${conviction} of 100`}>
              <circle cx="74" cy="74" r={RADIUS} fill="none" stroke="#0E1626" strokeWidth="13" />
              <circle
                className="gv-ring"
                cx="74" cy="74" r={RADIUS} fill="none"
                stroke={grade.color} strokeWidth="13" strokeLinecap="round"
                strokeDasharray={CIRC} strokeDashoffset={ringOff}
              />
            </svg>
            <div className="gv-gauge-center">
              <div className="gv-cv" style={{ color: grade.color }}>{conviction}</div>
              <div className="gv-den">/ 100</div>
            </div>
          </div>
          <div className="gv-gauge-cap">Conviction</div>
        </div>

        <div className="gv-lead">
          <span className={`gv-chip ${lclass}`}>{grade.label}</span>
          {grade.summary && <p className="gv-thesis">{grade.summary}</p>}
          <div className="gv-regime">Market regime: <b>{grade.regime}</b></div>
        </div>
      </div>

      {assetNote && <div className="gv-assetnote">{assetNote}</div>}

      {/* DCF fair-value read */}
      {dcf && (
        <div className="gv-dcf">
          <span className="gv-dcf-lab">DCF fair value</span>
          <span className="gv-dcf-val">
            ${dcf.fair_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="gv-dcf-verdict" style={{ color: dcfColor }}>{dcfVerdict}</span>
          <span className="gv-dcf-note">model estimate</span>
        </div>
      )}

      {/* Fund facts (ETFs / mutual funds) — shown in place of a company DCF */}
      {grade.fund_facts && (
        <div className="gv-dcf gv-funds">
          <span className="gv-dcf-lab">{at === 'mutual_fund' ? 'Fund facts' : 'ETF facts'}</span>
          {grade.fund_facts.expense_ratio_pct != null && (
            <span className="gv-fund-item"><b>{grade.fund_facts.expense_ratio_pct}%</b> expense</span>
          )}
          {grade.fund_facts.aum != null && (
            <span className="gv-fund-item"><b>{compactUSD(grade.fund_facts.aum)}</b> AUM</span>
          )}
          {grade.fund_facts.avg_volume != null && (
            <span className="gv-fund-item"><b>{compactNum(grade.fund_facts.avg_volume)}</b> avg vol</span>
          )}
        </div>
      )}

      {(at === 'etf' || at === 'mutual_fund') && <FundHoldings ticker={grade.ticker} />}

      {/* Pillar cards → click to expand the per-test work */}
      <div className="gv-pillars">
        {PILLAR_META.map((p) => {
          const detail = grade.pillars?.[p.key];
          const val = p.key === 'macro' ? grade.energy : grade[p.key];
          const pct = Math.max(0, Math.min(100, (val / p.max) * 100));
          const cls = bandClass(pct);
          const col = bandColor(cls);
          const disp = p.max === 100 ? Math.round(val) : Number(val).toFixed(1);
          const isOpen = expanded === p.key;
          const count = detail?.count;
          const computed = detail?.tests ? detail.tests.filter((t) => t.computable && t.score != null).length : 0;
          const phrase = ((!at || at === 'equity') ? PILLAR_PHRASE[p.key] : GENERIC_PHRASE)?.[cls] || '';
          const coverage = count ? (p.key === 'macro' ? `${count} tests` : `${computed}/${count} tests`) : '';
          return (
            <div className="gv-pillar" key={p.key}>
              <button className="gv-pillar-btn" onClick={() => setExpanded(isOpen ? null : p.key)} aria-expanded={isOpen}>
                <div className="gv-pillar-top">
                  <span className="gv-pillar-name">{p.label}</span>
                  <span className={`gv-chip sm ${cls}`}>{bandWord(cls)}</span>
                </div>
                <div className="gv-pillar-score">
                  <span className="gv-pillar-n" style={{ color: col }}>{disp}</span>
                  <span className="gv-pillar-d">/ {p.max}</span>
                  <span className="gv-pillar-chev">{isOpen ? '▾' : '▸'}</span>
                </div>
                <div className="gv-track"><span className="gv-fill" style={{ width: `${pct}%`, background: col }} /></div>
                <div className="gv-pillar-qual">{phrase}</div>
                <div className="gv-pillar-more">
                  {isOpen ? 'Hide the tests ▾' : `See the ${count ? `${count} ` : ''}tests ▸`}
                </div>
              </button>

              {isOpen && detail && (
                <div className="gv-tests">
                  {p.key === 'macro' ? (
                    <div className="gv-macro">
                      {detail.tests.map((t) => <span className="gv-macro-chip" key={t.label}>{t.label}</span>)}
                    </div>
                  ) : detail.tests.some((t) => t.computable && t.score != null) ? (
                    detail.tests.map((t) => {
                      const tp = t.computable && t.score != null ? Math.max(0, Math.min(100, t.score)) : 0;
                      const rawItems = formatRaw(t.raw);
                      const hasWhy = !!t.notes || rawItems.length > 0;
                      const testId = `${p.key}:${t.label}`;
                      const whyOpen = openTest === testId;
                      const tcls = bandClass(tp);
                      return (
                        <div key={t.label}>
                          <button
                            className="gv-test-btn"
                            style={{ cursor: hasWhy ? 'pointer' : 'default' }}
                            onClick={hasWhy ? () => setOpenTest(whyOpen ? null : testId) : undefined}
                            aria-expanded={whyOpen}
                          >
                            <span className="gv-test-chev">{hasWhy ? (whyOpen ? '▾' : '▸') : ''}</span>
                            <span className="gv-test-label">{t.label}</span>
                            <span className="gv-test-track"><span className="gv-test-fill" style={{ width: `${tp}%`, background: bandColor(tcls) }} /></span>
                            <span className="gv-test-val">{t.computable && t.score != null ? t.score : '—'}</span>
                          </button>
                          {whyOpen && hasWhy && (
                            <div className="gv-test-why">
                              {t.notes}
                              {rawItems.length > 0 && (
                                <div className="gv-test-raw">
                                  {rawItems.map((r) => <span className="gv-raw-chip" key={r.name}>{r.name}: {r.val}</span>)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="gv-test-note">{emptyPillarNote(grade.asset_type, p.label)}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 2026-10-04: the news behind the number, right under the grade. */}
      {showNews && <TickerNews symbol={grade.ticker} />}

      {grade.total_tests ? (
        <div className="gv-depth">
          🔬 {isEquity ? `${grade.total_tests} tests across 4 dimensions — recomputed live` : 'Recomputed live from market data'}
        </div>
      ) : null}

      {canChart && !teaser && (
        <>
          <button className="gv-chart-toggle" onClick={() => setShowChart((v) => !v)} aria-expanded={showChart}>
            {/* The CSS chevron carries the open/closed state now, so the label
                does not need its own caret. */}
            <span>{showChart ? 'Hide the chart' : '📊 See price vs. fundamentals'}</span>
          </button>
          {showChart && (
            <Suspense fallback={<div className="chart-loading" aria-busy="true" />}>
              <FundamentalsChart ticker={grade.ticker} />
            </Suspense>
          )}
        </>
      )}
    </div>
  );
}
